<template>
  <div>
    <a-space>
      <a-dropdown v-if="!sizeHide" :trigger="['click']">
        <a-button type="dashed" shape="circle" icon="column-height" />
        <template v-slot:overlay>
          <a-menu :selected-keys="[tableSize]" @click="changeSize">
            <a-menu-item key="default"> 默认 </a-menu-item>
            <a-menu-item key="middle"> 中等 </a-menu-item>
            <a-menu-item key="small"> 紧凑 </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>
      <a-popover v-if="!settingHide" placement="bottomRight" trigger="click">
        <template v-slot:content>
          <a-tree
            class="draggable-tree"
            :show-icon="true"
            v-model:value="checkedKeys"
            checkable
            :replaceFields="replaceFields"
            draggable
            :tree-data="currentValue"
            @drop="onDrop"
          />
        </template>
        <template v-slot:title>
          <a-checkbox
            :indeterminate="indeterminate"
            :checked="checkAll"
            @change="onCheckAllChange"
          >
            列展示/排序
            <a-tooltip>
              <template v-slot:title> 列排序需拖动名称 </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </a-checkbox>
        </template>
        <a-button type="dashed" shape="circle" icon="setting" />
      </a-popover>
      <a-button
        type="dashed"
        shape="circle"
        v-if="!refreshHide"
        :loading="refreshLoading"
        icon="reload"
        @click="refresh"
      />
    </a-space>
  </div>
</template>

<script lang="jsx">
import { $on, $off, $once, $emit } from '../../utils/gogocodeTransfer'
import Ellipsis from '@/components/Ellipsis'
import { QuestionCircleOutlined, PicLeftOutlined } from '@ant-design/icons-vue'

export default {
  name: 'NumberInfo',
  props: {
    tableSize: {
      type: String,
      default: 'default',
    },
    refreshLoading: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      required: true,
    },
    refreshHide: {
      type: Boolean,
      default: false,
    },
    settingHide: {
      type: Boolean,
      default: false,
    },
    sizeHide: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Ellipsis,
    QuestionCircleOutlined,
    PicLeftOutlined,
  },
  data() {
    return {
      allKey: [],
      indeterminate: true,
      checkAll: false,
      currentValue: [],
      checkedKeys: [],
      replaceFields: {
        children: 'children',
        title: 'currentTitle',
        key: 'dataIndex',
        slot: 'tsSlots',
      },
    }
  },
  mounted() {
    // 创建深拷贝，避免直接修改传入的 value
    // 使用浅拷贝数组，然后对每个对象进行浅拷贝（保留函数引用）
    this.currentValue = this.value.map(col => ({ ...col }))
    this.currentValue.forEach((column) => {
      this.allKey.push(column.dataIndex)
      column.currentTitle = (
        <ellipsis length={16} tooltip>
          {column.title}
        </ellipsis>
      )
      column.width2 = column.width
      column.ellipsis2 = column.ellipsis
      if (column.width !== 0 || !column.ellipsis) {
        this.checkedKeys.push(column.dataIndex)
      }
      column.switcherIcon = <PicLeftOutlined />
    })
  },
  watch: {
    checkedKeys(cur, last) {
      const checked = cur.filter(function (v) {
        return last.indexOf(v) === -1
      })
      const canceled = last.filter(function (v) {
        return cur.indexOf(v) === -1
      })
      // 创建新数组，避免直接修改响应式数据
      const newValue = this.currentValue.map((column) => {
        const newColumn = { ...column }
        if (checked.length > 0) {
          if (checked.indexOf(column.dataIndex) > -1) {
            newColumn['width'] = column.width2
            newColumn['ellipsis'] = column.ellipsis2
          }
        } else if (canceled.length > 0) {
          if (canceled.indexOf(column.dataIndex) > -1) {
            newColumn['width'] = 0
            newColumn['ellipsis'] = true
          }
        }
        return newColumn
      })
      // 检查是否有实际改变（比较关键属性）
      let hasChanged = false
      if (this.currentValue.length !== newValue.length) {
        hasChanged = true
      } else {
        hasChanged = newValue.some((newCol, index) => {
          const oldCol = this.currentValue[index]
          return !oldCol || 
            oldCol.dataIndex !== newCol.dataIndex ||
            oldCol.width !== newCol.width ||
            oldCol.ellipsis !== newCol.ellipsis
        })
      }
      
      if (hasChanged) {
        this.currentValue = newValue
        this.indeterminate = !!cur.length && cur.length < this.allKey.length
        this.checkAll = cur.length === this.allKey.length
        // 使用 setTimeout 延迟 emit，避免立即触发响应式更新
        setTimeout(() => {
          $emit(this, 'update:value', newValue)
        }, 0)
      } else {
        // 即使值没改变，也要更新状态标志
        this.indeterminate = !!cur.length && cur.length < this.allKey.length
        this.checkAll = cur.length === this.allKey.length
      }
    },
    value: {
      handler(newVal, oldVal) {
        // 当外部 value 变化时，更新 currentValue（但不触发 emit）
        // 只有当值真正改变时才更新，避免不必要的响应式触发
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          // 检查数组长度和内容是否真正改变
          const isChanged = !oldVal || 
            oldVal.length !== newVal.length || 
            newVal.some((col, index) => {
              const oldCol = oldVal[index]
              return !oldCol || 
                oldCol.dataIndex !== col.dataIndex ||
                oldCol.width !== col.width ||
                oldCol.ellipsis !== col.ellipsis
            })
          
          if (isChanged) {
            // 使用浅拷贝，保留函数引用
            this.currentValue = newVal.map(col => ({ ...col }))
          }
        }
      },
      deep: false, // 改为 false，避免深度监听导致递归
      immediate: false
    }
  },
  methods: {
    changeSize(e) {
      $emit(this, 'update:tableSize', e.key)
    },
    onCheckAllChange(e) {
      Object.assign(this, {
        checkedKeys: e.target.checked ? this.allKey : [],
        indeterminate: false,
        checkAll: e.target.checked,
      })
    },
    refresh() {
      $emit(this, 'refresh')
    },
    onDrop(info) {
      const dropKey = info.node.eventKey
      const dragKey = info.dragNode.eventKey
      const dropPos = info.node.pos.split('-')
      const dropPosition =
        info.dropPosition - Number(dropPos[dropPos.length - 1])
      const loop = (data, key, callback) => {
        data.forEach((item, index, arr) => {
          if (item.dataIndex === key) {
            return callback(item, index, arr)
          }
          if (item.children) {
            return loop(item.children, key, callback)
          }
        })
      }
      const data = [...this.value]

      // Find dragObject
      let dragObj
      loop(data, dragKey, (item, index, arr) => {
        arr.splice(index, 1)
        dragObj = item
      })
      let ar
      let i
      loop(data, dropKey, (item, index, arr) => {
        ar = arr
        i = index
      })
      if (dropPosition === -1) {
        ar.splice(i, 0, dragObj)
      } else {
        ar.splice(i + 1, 0, dragObj)
      }
      $emit(this, 'update:value', data)
      this.currentValue = data
    },
  },
  emits: ['update:value', 'update:tableSize', 'refresh'],
}
</script>
